import React from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, RotateCcw, Info, User } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useI18n } from "../context/I18nContext";

/**
 * Mobile-first sticky header (no hamburger).
 * Visible on all viewports. Thumb-reachable on mobile.
 */
const Navbar = () => {
  const { count, setOpen } = useCart();
  const { t } = useI18n();

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 bg-[#FFFBF5]/95 backdrop-blur border-b border-[#F39C12]/15"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-5 h-14 sm:h-16 flex items-center justify-between gap-2">
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <span className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-[#D63031]">
            Gruham
          </span>
        </Link>

        {/* Middle: 7-day return + about (about hidden on smallest screens) */}
        <nav className="flex items-center gap-1 sm:gap-2">
          <NavLink
            to="/returns-refunds"
            data-testid="nav-return-7days"
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-full text-[11px] sm:text-xs font-semibold transition ${
                isActive
                  ? "bg-[#27AE60]/15 text-[#27AE60]"
                  : "bg-[#27AE60]/10 text-[#27AE60] hover:bg-[#27AE60]/20"
              }`
            }
          >
            <RotateCcw className="w-3.5 h-3.5" strokeWidth={2.2} />
            <span className="whitespace-nowrap">{t("return_7days")}</span>
          </NavLink>

          <NavLink
            to="/about"
            data-testid="nav-about"
            className={({ isActive }) =>
              `hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition ${
                isActive
                  ? "bg-[#FFF5E1] text-[#D63031]"
                  : "text-[#2C3E50] hover:bg-[#FFF5E1]"
              }`
            }
          >
            <Info className="w-3.5 h-3.5" />
            {t("about_us")}
          </NavLink>

          {/* My Order (customer "login" – opens Track Order which uses phone/order-# lookup) */}
          <Link
            to="/track-order"
            data-testid="nav-my-order"
            className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 h-10 rounded-full bg-white border border-[#F39C12]/30 hover:border-[#F39C12] transition text-[11px] sm:text-xs font-semibold text-[#2C3E50]"
            aria-label="My Order"
            title="My Order"
          >
            <User className="w-4 h-4 text-[#D63031]" />
            <span className="hidden sm:inline">My Order</span>
          </Link>

          {/* Cart */}
          <button
            data-testid="cart-open-btn"
            onClick={() => setOpen(true)}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-[#F39C12]/30 hover:border-[#F39C12] transition"
            aria-label={t("cart")}
          >
            <ShoppingBag className="w-5 h-5 text-[#2C3E50]" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1 -right-1 bg-[#D63031] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
